'use client';

import { memo, useRef } from 'react';
import Link from 'next/link';
import { Box, Tooltip, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { alpha } from '@mui/material/styles';
import { formatDob } from '@/lib/dateTime';
import { AnimalSexBadge } from '@/hooks/publicSearch/animal/helpers';
import { ANIMAL_SEX } from '@/components/animals/animalSex';
import { IDENT_SIZE, NAME_SIZE } from './constants';
import { getGenerationLabel } from './helpers';
import { GridCell } from './types';

/** One card in the grid: subject, ancestor (with tooltip), or null placeholder. */
function PedigreeCard({
  cell,
  nodeHrefPrefix,
  linkRootAnimal,
  isMobile,
  showDetails,
  selected,
  interactive,
  assignable,
  onSelect,
}: {
  cell: GridCell;
  /** Base path for animal links; empty disables linking. Final href is `${prefix}/${node.id}`. */
  nodeHrefPrefix: string;
  linkRootAnimal?: boolean;
  isMobile: boolean;
  showDetails: boolean;
  selected?: boolean;
  interactive?: boolean;
  assignable?: boolean;
  onSelect?: () => void;
}) {
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const { node, depth, isSireLine } = cell;
  const isSubject = depth === 0;
  const nameFontSize = NAME_SIZE[depth] ?? '10px';
  const identFontSize = IDENT_SIZE[depth] ?? '9px';
  const detailFontSize = depth <= 1 ? '9px' : '7px';

  const sex = node?.sex?.trim().toUpperCase() ?? '';
  const isMale = sex.startsWith(ANIMAL_SEX.MALE);
  const isFemale = sex.startsWith(ANIMAL_SEX.FEMALE);

  const tooltipContent = node ? (
    <Box sx={{ fontSize: '12px', lineHeight: 1.6 }}>
      <Box>
        <strong>Name:</strong> {node.name || '—'}
      </Box>
      <Box>
        <strong>Sex:</strong> {node.sex || '—'}
      </Box>
      <Box>
        <strong>DOB:</strong> {formatDob(node.dob)}
      </Box>
      <Box>
        <strong>Status:</strong> {node.status || '—'}
      </Box>
    </Box>
  ) : (
    ''
  );

  const cardBox = (
    <Box
      sx={(theme) => {
        const sireColor = theme.palette.info.main;
        const damColor = theme.palette.error.main;
        const connectorColor = theme.palette.text.secondary;
        const getSubjectBorderColor = () => {
          if (isMale) return sireColor;
          if (isFemale) return damColor;
          return connectorColor;
        };
        const subjectBorderColor = getSubjectBorderColor();
        const accentColor = isSireLine ? sireColor : damColor;

        const getSubjectBackgroundColor = () => {
          if (isMale) return alpha(sireColor, 0.12);
          if (isFemale) return alpha(damColor, 0.12);
          return alpha(connectorColor, 0.08);
        };

        return {
          height: '100%',
          px: 0.75,
          py: 0.5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          borderRadius: '4px',
          border: '1px solid',
          overflow: 'hidden',
          cursor: interactive && onSelect ? 'pointer' : undefined,
          ...(selected
            ? {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 1,
              }
            : {}),
          ...(isSubject
            ? {
                borderColor: subjectBorderColor,
                backgroundColor: getSubjectBackgroundColor(),
              }
            : {
                borderColor: isSireLine
                  ? alpha(sireColor, 0.25)
                  : alpha(damColor, 0.25),
                borderLeft: `3px solid ${accentColor}`,
                backgroundColor: isSireLine
                  ? alpha(sireColor, 0.06)
                  : alpha(damColor, 0.06),
              }),
          // Staged (unsaved) link: dashed border sets it apart from committed
          // ancestors until "Save and validate" persists it.
          ...(node?.pending ? { borderStyle: 'dashed' } : {}),
        };
      }}
    >
      {node ? (
        <>
          {node.pending && (
            <Typography
              sx={{
                fontSize: '7px',
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: 'warning.main',
                lineHeight: 1,
                mb: 0.25,
              }}
            >
              Unsaved
            </Typography>
          )}
          {isMobile && node.sex && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                overflow: 'hidden',
                maxHeight: showDetails ? '36px' : '0px',
                opacity: showDetails ? 1 : 0,
                mb: showDetails ? 0.25 : 0,
                transition:
                  'max-height 0.3s ease, opacity 0.25s ease, margin-bottom 0.3s ease',
              }}
            >
              <AnimalSexBadge sex={node.sex} />
            </Box>
          )}
          <Typography
            sx={{
              fontSize: nameFontSize,
              fontWeight: isSubject ? 600 : 500,
              color: 'text.primary',
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {node.name || '—'}
          </Typography>
          {node.ident && (
            <Typography
              sx={{
                fontSize: identFontSize,
                color: 'text.secondary',
                lineHeight: 1.2,
                wordBreak: 'break-word',
              }}
            >
              {node.ident}
            </Typography>
          )}
          {isMobile && (
            <Box
              sx={{
                overflow: 'hidden',
                maxHeight: showDetails ? '60px' : '0px',
                opacity: showDetails ? 1 : 0,
                mt: showDetails ? 0.25 : 0,
                transition:
                  'max-height 0.3s ease, opacity 0.25s ease, margin-top 0.3s ease',
              }}
            >
              {node.dob && (
                <Typography
                  sx={{
                    fontSize: detailFontSize,
                    color: 'text.secondary',
                    lineHeight: 1.2,
                  }}
                >
                  DOB: {formatDob(node.dob)}
                </Typography>
              )}
              {node.status && (
                <Typography
                  sx={{
                    fontSize: detailFontSize,
                    color: 'text.secondary',
                    lineHeight: 1.2,
                  }}
                >
                  Status: {node.status}
                </Typography>
              )}
            </Box>
          )}
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
          }}
        >
          {interactive && !assignable ? (
            <LockOutlinedIcon
              sx={{ fontSize: identFontSize, color: 'text.disabled' }}
            />
          ) : null}
          <Typography
            sx={{
              fontSize: nameFontSize,
              color: assignable ? 'primary.main' : 'text.disabled',
              fontStyle: assignable ? 'normal' : 'italic',
              fontWeight: assignable ? 600 : 400,
              lineHeight: 1.3,
            }}
          >
            {emptySlotLabel(cell.path, !!assignable)}
          </Typography>
        </Box>
      )}
    </Box>
  );

  // Skip the hover tooltip in interactive mode: the registration tree renders the
  // selection bar directly above the cards, and a top-placed tooltip overlaps it.
  const wrappedBox =
    isMobile || interactive ? (
      cardBox
    ) : (
      <Tooltip
        title={node ? tooltipContent : ''}
        placement="top"
        arrow
        enterDelay={300}
        disableTouchListener
        slotProps={{
          popper: {
            modifiers: [{ name: 'offset', options: { offset: [0, -8] } }],
          },
        }}
      >
        <Box sx={{ height: '100%' }}>{cardBox}</Box>
      </Tooltip>
    );

  // Subject never links unless `linkRootAnimal`. In interactive mode, selection wins over navigation.
  const href =
    !interactive &&
    node &&
    node.id &&
    nodeHrefPrefix &&
    (!isSubject || linkRootAnimal)
      ? `${nodeHrefPrefix}/${node.id}`
      : null;

  if (interactive && onSelect) {
    return (
      <Box
        component="button"
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        aria-label={describeCellLabel(cell, node, assignable)}
        title={
          !node && !assignable
            ? 'Locked. Save the parent on this branch first.'
            : undefined
        }
        sx={{
          display: 'block',
          width: '100%',
          height: '100%',
          p: 0,
          m: 0,
          border: 'none',
          background: 'none',
          textAlign: 'inherit',
          font: 'inherit',
        }}
      >
        {wrappedBox}
      </Box>
    );
  }

  if (href && node) {
    const getLineLabel = () => {
      if (isSubject) return '';
      return isSireLine ? ', sire line' : ', dam line';
    };
    const lineLabel = getLineLabel();
    const nodeLabel = `${node.name || 'Unknown'}${node.ident ? ` (${node.ident})` : ''}, ${getGenerationLabel(depth)}${lineLabel}`;
    return (
      <Link
        href={href}
        aria-label={nodeLabel}
        style={{ display: 'block', height: '100%', textDecoration: 'none' }}
        onTouchStart={(touchEvent) => {
          const touch = touchEvent.touches[0];
          if (!touch) return;
          touchStartX.current = touch.clientX;
          touchStartY.current = touch.clientY;
        }}
        onTouchEnd={(touchEvent) => {
          const touch = touchEvent.changedTouches[0];
          if (!touch) return;
          // >8px in either axis = scroll, not tap. iOS Safari would otherwise
          // fire navigation when the user releases after a horizontal scroll.
          const deltaX = Math.abs(touch.clientX - touchStartX.current);
          const deltaY = Math.abs(touch.clientY - touchStartY.current);
          if (deltaX > 8 || deltaY > 8) {
            touchEvent.preventDefault();
          }
        }}
      >
        {wrappedBox}
      </Link>
    );
  }

  return <Box sx={{ height: '100%' }}>{wrappedBox}</Box>;
}

/**
 * Label for an empty slot at any depth. `path` ends in 's' (sire side) or 'd'
 * (dam side); assignable slots invite an action, locked ones just name the role.
 */
function emptySlotLabel(path: string, assignable: boolean): string {
  const side = path.at(-1);
  const role = side === 's' ? 'sire' : side === 'd' ? 'dam' : 'ancestor';
  if (role === 'ancestor') return 'Unknown';
  const capitalised = role === 'sire' ? 'Sire' : 'Dam';
  return assignable ? `Assign ${role}` : capitalised;
}

function describeCellLabel(
  cell: GridCell,
  node: GridCell['node'],
  assignable?: boolean,
) {
  if (!node) {
    const side = cell.path.at(-1);
    const role = side === 's' ? 'sire' : side === 'd' ? 'dam' : 'ancestor';
    if (!assignable) return `${role} slot, locked`;
    return `Assign ${role}`;
  }
  const line = cell.isSireLine ? 'sire line' : 'dam line';
  return `${node.name || 'Unknown'}${node.ident ? ` (${node.ident})` : ''}, ${getGenerationLabel(cell.depth)}, ${line}`;
}

export default memo(PedigreeCard);
